import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Download } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ArtifactExportFormat } from '@/artifact/export';
import {
  PAPER_CONTROL_RADIUS_CLASS,
  PAPER_ICON_MICRO_CLASS,
  PAPER_ICON_STROKE_WIDTH,
  PAPER_ICON_TINY_CLASS,
} from '@/components/paper/paperShapes';

const FORMAT_LABEL: Record<ArtifactExportFormat, string> = {
  png: 'PNG',
  pdf: 'PDF',
  html: 'HTML',
};

interface MenuPosition {
  top: number;
  right: number;
  minWidth: number;
}

interface ExportFormatButtonsProps {
  formats: ArtifactExportFormat[];
  disabled: boolean;
  onDownload: (format: ArtifactExportFormat) => void;
  /**
   * Tailwind classes applied to the trigger button. The previous
   * multi-button API split per-format styling here; we keep the same
   * prop name (and contract) so existing call sites read naturally.
   */
  buttonClassName?: string;
  /** When true, hide the "Export" label and chevron — icon-only trigger. */
  compact?: boolean;
}

/**
 * Single "Export" trigger that opens a small format menu when clicked.
 *
 * Replaces the old row of one-button-per-format. The trigger stays
 * styled by the caller via `buttonClassName`; the dropdown panel is
 * rendered into `document.body` via a portal so callers don't have to
 * reason about clipping (the panel chrome and the right-rail pill both
 * use `overflow: hidden`, which would otherwise hide the menu).
 */
export const ExportFormatButtons = (props: ExportFormatButtonsProps) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<MenuPosition | null>(null);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    const computePos = () => {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setPos({
        top: rect.bottom + 6,
        right: window.innerWidth - rect.right,
        minWidth: Math.max(rect.width, 120),
      });
    };
    computePos();
    window.addEventListener('resize', computePos);
    window.addEventListener('scroll', computePos, true);
    return () => {
      window.removeEventListener('resize', computePos);
      window.removeEventListener('scroll', computePos, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handlePick = (format: ArtifactExportFormat) => {
    setOpen(false);
    props.onDownload(format);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={props.disabled}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Export"
        className={cn(
          'flex items-center gap-1 text-[11px] font-medium uppercase text-zinc-500 transition-colors disabled:opacity-40',
          props.buttonClassName,
        )}
      >
        <Download className={PAPER_ICON_MICRO_CLASS} strokeWidth={PAPER_ICON_STROKE_WIDTH} />
        {!props.compact && (
          <>
            Export
            <ChevronDown
              className={cn(
                PAPER_ICON_MICRO_CLASS,
                'transition-transform duration-150',
                open && 'rotate-180',
              )}
              strokeWidth={PAPER_ICON_STROKE_WIDTH}
            />
          </>
        )}
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          aria-label="Export format"
          style={{
            position: 'fixed',
            top: pos?.top ?? -9999,
            right: pos?.right ?? -9999,
            minWidth: pos?.minWidth ?? 120,
            visibility: pos ? 'visible' : 'hidden',
            zIndex: 80,
          }}
          className={cn(
            'flex flex-col p-[4px] rounded-lg shadow-lg',
            'bg-[var(--surface)] border border-solid border-[var(--hair)]',
          )}
        >
          {props.formats.map((fmt) => (
            <button
              key={fmt}
              type="button"
              role="menuitem"
              disabled={props.disabled}
              onClick={() => handlePick(fmt)}
              className={cn(
                'flex items-center gap-[8px] w-full px-[8px] py-[6px]',
                PAPER_CONTROL_RADIUS_CLASS,
                'text-[12px] text-[var(--fg-2)] cursor-pointer text-left',
                'transition-colors duration-150',
                'hover:bg-[var(--bg-alt)] hover:text-[var(--fg)]',
                'disabled:opacity-40 disabled:cursor-not-allowed',
              )}
            >
              <Download
                className={cn('shrink-0', PAPER_ICON_TINY_CLASS)}
                strokeWidth={PAPER_ICON_STROKE_WIDTH}
              />
              {FORMAT_LABEL[fmt]}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </>
  );
};
